import { Link } from "@tanstack/react-router";
import { ReactNode } from "react";
import { ShieldCheck, FileText, Cookie } from "lucide-react";

interface LegalLayoutProps {
  title: string;
  subtitle?: string;
  lastUpdated: string;
  children: ReactNode;
}

const legalLinks = [
  { to: "/privacy", label: "Política de Privacidad", icon: ShieldCheck },
  { to: "/terms", label: "Términos y Condiciones", icon: FileText },
  { to: "/cookies", label: "Política de Cookies", icon: Cookie },
] as const;

export function LegalLayout({ title, subtitle, lastUpdated, children }: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[220px_1fr] lg:gap-16">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Legal
            </p>
            <nav className="flex flex-col gap-1">
              {legalLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
                    activeProps={{
                      className:
                        "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-semibold bg-surface text-foreground",
                    }}
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-6 rounded-lg border border-border bg-surface p-4 text-xs text-muted-foreground">
              <p className="font-medium text-foreground">¿Necesitás ayuda?</p>
              <p className="mt-1.5">
                Escribinos a{" "}
                <a
                  href="mailto:legal@zyntratickets.com"
                  className="text-primary hover:underline"
                >
                  legal@zyntratickets.com
                </a>
              </p>
            </div>
          </aside>

          {/* Content */}
          <article className="min-w-0">
            <header className="mb-10 border-b border-border pb-8">
              <p className="text-sm font-medium text-primary">Documentos legales</p>
              <h1 className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-3 text-lg text-muted-foreground">{subtitle}</p>
              )}
              <p className="mt-4 text-sm text-muted-foreground">
                Última actualización: <span className="text-foreground">{lastUpdated}</span>
              </p>
            </header>

            <div className="legal-prose">{children}</div>

            <footer className="mt-16 border-t border-border pt-8 text-sm text-muted-foreground">
              <p>
                © {new Date().getFullYear()} Zyntra Events S.R.L. — Todos los derechos
                reservados. San José, Costa Rica.
              </p>
              <p className="mt-2">
                Para consultas sobre este documento, escribinos a{" "}
                <a href="mailto:legal@zyntratickets.com" className="text-primary hover:underline">
                  legal@zyntratickets.com
                </a>
                .
              </p>
            </footer>
          </article>
        </div>
      </div>
    </div>
  );
}
